import React, { useState, useEffect, useRef } from "react";
import { ArrowIconClosed } from "../../../icons/Icons";
import "./Select.scss";

type ComboSelectProps = {
  options: string[];
  selectedOption: string;
  setSelectedOption: (value: string) => void;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
};

const ComboSelect = ({
  options,
  selectedOption,
  setSelectedOption,
  placeholder = "Select or type to add new...",
  fullWidth,
  disabled,
  required = false,
}: ComboSelectProps) => {
  const [optionsDisplayed, setOptionsDisplayed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<string[]>(options);
  const [showCreateOption, setShowCreateOption] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOptionsDisplayed(false);
        setSearchTerm("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter((option) =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
      
      const exactMatch = options.some(
        (option) => option.toLowerCase() === searchTerm.toLowerCase()
      );
      setShowCreateOption(!exactMatch && searchTerm.trim().length > 0);
    } else {
      setFilteredOptions(options);
      setShowCreateOption(false);
    }
  }, [searchTerm, options]);

  const changeOption = (value: string) => {
    setSearchTerm("");
    setSelectedOption(value);
    setOptionsDisplayed(false);
    setShowCreateOption(false);
  };

  const createNewOption = () => {
    const newValue = searchTerm.trim();
    if (newValue) {
      setSelectedOption(newValue);
      setSearchTerm("");
      setOptionsDisplayed(false);
      setShowCreateOption(false);
    }
  };

  return (
    <div className={`so-select combo-select ${fullWidth && "full-width"}`} ref={ref}>
      <div
        className={`select-value-container ${disabled ? "disabled" : ""} ${
          optionsDisplayed ? "options-displayed" : ""
        } ${required && !selectedOption ? "required-empty" : ""}`}
        onClick={() => {
          if (disabled) return;
          setOptionsDisplayed(!optionsDisplayed);
          if (!optionsDisplayed) {
            setTimeout(() => {
              const input = ref.current?.querySelector(".select-search") as HTMLInputElement;
              input?.focus();
            }, 0);
          }
        }}
      >
        {!optionsDisplayed && (
          <span className="select-value">{selectedOption || placeholder}</span>
        )}

        {optionsDisplayed && (
          <input
            type="text"
            className="select-search"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && showCreateOption) {
                e.preventDefault();
                createNewOption();
              }
            }}
          />
        )}
        <span
          className={`dropdown-icon ${disabled ? "disabled" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            setOptionsDisplayed(!optionsDisplayed);
            if (!optionsDisplayed) {
              setTimeout(() => {
                const input = ref.current?.querySelector(".select-search") as HTMLInputElement;
                input?.focus();
              }, 0);
            }
          }}
        >
          <ArrowIconClosed />
        </span>
      </div>

      {optionsDisplayed && (
        <div className={`select-options ${optionsDisplayed ? "options-displayed" : "options-hidden"}`}>
          {showCreateOption && (
            <div
              className="option create-new-option"
              onClick={createNewOption}
            >
              <strong>+ Create:</strong> "{searchTerm}"
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option}
                className={`option ${option === selectedOption ? "active" : ""}`}
                onClick={() => changeOption(option)}
              >
                {option}
              </div>
            ))
          ) : (
            !showCreateOption && (
              <div className="option no-options">No matching options</div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ComboSelect;
